/* eslint react/no-access-state-in-setstate: 0 */

import * as React from 'react';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core';
import update from 'immutability-helper';
import { withRouter } from 'react-router';
import styles from './templateEditor.styles';
import {
  get, pro, compose, isDefined,
} from '../../../utility';
import AttributesPanel from '../attributes/attributesPanel';
import ConfirmMultiDeleteDialog from '../confirmMultiDeleteDialog';
import { withDialog } from '../../../shared/dialog/withDialog';
import c, {
  EDITOR_MODE,
  ZOOM_MINIMUM,
  ZOOM_MAXIMUM,
  ZOOM_PRECISION_FACTOR,
  DEFAULT_ZOOM,
  DEFAULT_TEXTBOX_HEIGHT,
  DEFAULT_TEXTBOX_WIDTH,
  DEFAULT_DATE_HEIGHT,
  DEFAULT_DATE_WIDTH,
  DEFAULT_CHECKBOX_HEIGHT,
  DEFAULT_CHECKBOX_WIDTH,
  DEFAULT_SIGNATURE_WIDTH,
  DEFAULT_SIGNATURE_HEIGHT,
  DEFAULT_LINE_LENGTH,
  DEFAULT_INITIAL_HEIGHT,
  DEFAULT_INITIAL_WIDTH,
  DUPLICATION_OFFSET, PAGE_LIST_MIN_WIDTH, PAGE_SIDE_MARGIN,
} from '../../constants';
import Toolbar from '../toolbar/toolbar';
import Banner from '../banner';
import PageList from '../pageList';
import {
  adaptFields,
  checkMoveBoundaries,
} from './templateEditor.utils';
import {
  determineRelativePositions,
  generateColorMap,
  getLineRectangle,
  preparePages,
  generateDataFieldByType,
} from '../utils';
import PageManager from '../pageManager';
import { withSnackbar } from '../../../shared/snackbar/withSnackbar';
import { fieldTemplates } from './fieldTemplates';
import type {
  Data,
  LineField,
  AdaptedPage,
  AdaptedField,
  FieldTemplate,
  EditorDocument,
} from '../flowTypes';
import type { CreatePageFieldsResponse } from '../../../agent-panel/api/mutations/createDocPageFields';
import type { UpdateDocPageFieldsResponse } from '../../../agent-panel/api/mutations/updateDocPageFields';
import type { SessionSignee } from '../../../agent-panel/api/fragments/sessionSignee';
import { withAppUser } from '../../../shared/authorization';
import type { AppUser } from '../../../shared/authorization';
import type { UserStampInput, CreateUserStampResponse } from '../../../agent-panel/api/mutations/createUserStamp';
import { withPinchZoom } from '../../../shared/pinchZoom';
import axios from 'axios'
import { ESIGN_ENDPOINT } from '../../../configurations';
const REACT_VIRTUALIZED_SCROLLTOP_UPDATE_TIME = 150;
const MAX_FIELDS_PER_PAGE = 500;

type PropType = {
  classes: Object,
  mode: 'template-draft' | 'template-published',
  nextDocumentId: string,
  previousDocumentId: string,
  handleDocumentSwitch: Function,
  addValues: (Object, number) => void,
  values: { [number]: any},
  handleFieldUpdate: Function,
  handleBlur: Function,
  handleChange: (string) => void,
  setFieldValue: (string, any) => void,
  createSnackbar: (React.Element | string) => void,
  createFields: (fields: Array<FieldTemplate>, pageId: number, docId: number) => Promise<CreatePageFieldsResponse>,
  updateFields: (fields: Array<AdaptedField>, docId: number, ?{ deleting: boolean }) => Promise<UpdateDocPageFieldsResponse>,
  createDialog: Function,
  closeDialog: Function,
  documents: Array<EditorDocument>,
  signees: ?Array<?SessionSignee>,
  user: AppUser,
  pdfPages: Object,
  createStampsData: (docId: number, value: string, assignee: string, ?{ isInitials: boolean }) => Promise<Data>,
  createUserStamps: ({ userSignature: UserStampInput, userInitial: UserStampInput }) => Promise<CreateUserStampResponse>,
  signatureReferences: { [number]: number },
  initialsReferences: { [number]: number },
  refetchUser: Function,
  setUser: Function,
  signeeCheck: (checkAllSigneesHaveField: Function) => void,
  pinchedScale: number,
  pages: Array<AdaptedPage>,
  updatePages: Function,
  currentSignee: ?string,
  onCurrentSigneeChange: Function,
};

type State = {
  activeSelectionPageFields: Array<number>,
  activeSelectionRelPos: { [number]: { x: number, y: number } },
  activeSelectionBox: { left: number, top: number },
  // Index of an active page
  activePage: number,
  floatingFieldType: ?string,
  zoom: number,
  listScrollTop: any,
  placedSignature: boolean,
  zoomAdjustmentFactor: number,
};

export class TemplateEditorC extends React.PureComponent<PropType, State> {

  state = {
    activePage: 0,
    activeSelectionPageFields: [],
    activeSelectionRelPos: {},
    activeSelectionBox: { left: 0, top: 0 },
    floatingFieldType: null,
    zoom: DEFAULT_ZOOM,
    listScrollTop: null,
    placedSignature: false,
    zoomAdjustmentFactor: 1,
  };

  pageManager = new PageManager();

  // Stores all refs from child pages.
  pageRefs = {};

  // Stores React Virtualized scroll values to adjust for zoom.
  listScrollValues = {};

  // fields buffer
  clipboardFields: Array<{
    pageIndex: number,
    pageField: AdaptedField,
    relativeTop: number,
    relativeLeft: number,
  }> = [];

  // colors for color coding
  colorMap = this.props.signees && generateColorMap(
    this.props.signees,
  );

  // current user session signee name
  mySessionSigneeName = (() => {
    const mySessionSignee = this.props.signees && this.props.signees.find(s => s && s.userByUserId && s.userByUserId.email === this.props.user.email);
    return mySessionSignee ? mySessionSignee.sessionSigneeName : null;
  })();

  scaleZoomToFitPage = () => {
    // If first page of the first document is too wide, adjust the overall zoom.
    if (this.pageListRef) {
      const bufferWidth = 10;
      const { width: pageListWidth } = this.pageListRef.getBoundingClientRect();

      // If height is changed,we don't care.
      if (pageListWidth === this.pageListWidth) return;

      // Grab either actual pagelist width, or, if it's too small, grab the const.
      const referenceWidth = pageListWidth > PAGE_LIST_MIN_WIDTH ? pageListWidth : PAGE_LIST_MIN_WIDTH;
      const zoomAdjustmentFactor = this.props.documents.length
        && this.props.documents[0].pages
        && this.props.documents[0].pages.nodes[0]
        && this.props.documents[0].pages.nodes[0].width
        && (this.props.documents[0].pages.nodes[0].width + (PAGE_SIDE_MARGIN + bufferWidth) * 2) > referenceWidth
        ? (referenceWidth - (PAGE_SIDE_MARGIN + bufferWidth) * 2) / this.props.documents[0].pages.nodes[0].width
        : 1;

      this.pageListWidth = referenceWidth;

      this.setState({
        zoom: DEFAULT_ZOOM * zoomAdjustmentFactor,
        zoomAdjustmentFactor,
      });
    }
  };

  componentDidMount() {
    this.props.signeeCheck(this.checkAllSigneesHaveField);

    window.addEventListener('resize', this.scaleZoomToFitPage);
    this.scaleZoomToFitPage();
  }

  componentDidUpdate(prevProps: PropType) {
    if (this.props.documents !== prevProps.documents) {
      this.props.updatePages(preparePages(this.props.documents))
      this.setState({
        activePage: 0,
        activeSelectionPageFields: [],
        activeSelectionRelPos: {},
        activeSelectionBox: { left: 0, top: 0 },
        floatingFieldType: null,
      });
      this.clearClipboard();
    }

    // If we're on mobile and user pinched in to zoom.
    if (this.props.pinchedScale !== prevProps.pinchedScale) {
      this.setState({
        zoom: this.props.pinchedScale * ZOOM_PRECISION_FACTOR * this.state.zoomAdjustmentFactor,
      });
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.scaleZoomToFitPage);
  }

  passPageRefToEditor = (ref: HTMLDivElement, pageIndex: number) => {
    this.pageRefs[pageIndex] = ref;
  };

  setCurrentPageOnMouseMove = (pageIndex: number) => {
    if (pageIndex !== this.state.activePage) {
      this.setState({
        activePage: pageIndex,
      });
      this.pageRefs[pageIndex].focus({ preventScroll: true });
    }
  };

  // Function is used to add field to the page. Optionally, can accept
  // width and height of the field (x2, y2 for a line), otherwise, the default values will be used.
  handleFieldAdd = async (type: string, top: number, left: number, pageIndex: number, {
    width, height, x2, y2,
  }: {width: number, height: number, x2: number, y2: number} = {}) => {

    let serverTimeStamp; // we always have to set current Date based upon server time
    if (type === c.ItemTypes.DATE) {
      const { data } = await axios.get(`${ESIGN_ENDPOINT}/timeStamp`);
      serverTimeStamp = data.timeStamp;
    }
    
    let locTop = top;
    let locLeft = left;

    let fieldHeight;
    let fieldWidth;
    let stampDataReference;
    let shouldUpdateLineStartPoint = false;
    const { signatureReferences, initialsReferences } = this.props;
    const zoom = this.state.zoom / ZOOM_PRECISION_FACTOR;
    const page = this.props.pages[pageIndex];
    const pageRect = this.pageRefs[pageIndex].getBoundingClientRect();
    switch (type) {
      case c.ItemTypes.CHECKBOX:
        fieldWidth = width || DEFAULT_CHECKBOX_WIDTH;
        fieldHeight = height || DEFAULT_CHECKBOX_HEIGHT;
        break;
      case c.ItemTypes.TEXT:
        fieldWidth = width || DEFAULT_TEXTBOX_WIDTH;
        fieldHeight = height || DEFAULT_TEXTBOX_HEIGHT;
        break;
      case c.ItemTypes.DATE:
        fieldWidth = width || DEFAULT_DATE_WIDTH;
        fieldHeight = height || DEFAULT_DATE_HEIGHT;
        break;
      case c.ItemTypes.SIGNATURE:
        fieldWidth = width || DEFAULT_SIGNATURE_WIDTH;
        fieldHeight = height || DEFAULT_SIGNATURE_HEIGHT;
        break;
      case c.ItemTypes.LINE:
        if (isDefined(x2) && isDefined(y2)) {
          const {
            width: lineWidth, height: lineHeight,
          } = getLineRectangle(left, top, x2, y2);

          if ((lineWidth === lineHeight) && (lineWidth === 0)) {
            // Line has been dropped to the page and has zero width and zero height, so we will set the default width to it
            // Also will notify that the line start point should be moved if the end is out of the page
            fieldWidth = DEFAULT_LINE_LENGTH;
            fieldHeight = 0;
            shouldUpdateLineStartPoint = true;
          }
        }
        break;
      case c.ItemTypes.INITIAL:
        fieldWidth = width || DEFAULT_INITIAL_WIDTH;
        fieldHeight = height || DEFAULT_INITIAL_HEIGHT;
        break;
      default:
    }

    // if part of the field is outside of the page, stick it to the page border
    // use non-zoomed pageRect values in calculations
    if ((locTop + fieldHeight) > pageRect.height / zoom) {
      locTop -= ((locTop + fieldHeight) - pageRect.height / zoom);
    }

    if ((locLeft + fieldWidth) > pageRect.width / zoom) {
      locLeft -= ((locLeft + fieldWidth) - pageRect.width / zoom);
    }

    // for signature field, grab the dataReference of the corresponding docData, if it's been created
    if (type === c.ItemTypes.SIGNATURE && this.mySessionSigneeName === this.props.currentSignee) {
      stampDataReference = signatureReferences[page.documentId];
    }

    if (type === c.ItemTypes.INITIAL && this.mySessionSigneeName === this.props.currentSignee) {
      stampDataReference = initialsReferences[page.documentId];
    }

    const field = fieldTemplates[type]({
      x: shouldUpdateLineStartPoint ? Math.round(locLeft) : Math.round(left),
      y: shouldUpdateLineStartPoint ? Math.round(locTop) : Math.round(top),
      width,
      height,
      x2: shouldUpdateLineStartPoint ? Math.round(locLeft) + fieldWidth : x2,
      y2: shouldUpdateLineStartPoint ? Math.round(locTop) + fieldHeight : y2,
      assignee: this.props.currentSignee,
      // for date field, if the field is assigned to me, get the current date
      dateValue: type === c.ItemTypes.DATE && this.mySessionSigneeName === this.props.currentSignee ? serverTimeStamp : '',
      dataReference: (type === c.ItemTypes.SIGNATURE || type === c.ItemTypes.INITIAL) && stampDataReference,
    });

    this.createNewFields([field], pageIndex);
    this.pageRefs[pageIndex].focus({ preventScroll: true });
  };

  handleFieldsUpdate = async (fields: Array<AdaptedField>, { sign }: { sign: false } = {}) => {
    const { pages } = this.props;
    const { activePage } = this.state;
    const docId = pages[activePage].documentId;

    const [err, data] = await pro(this.props.updateFields(fields, docId));

    if (err) {
      this.props.createSnackbar(c.CANNOT_UPDATE_FIELD);
    } else {
      if (sign) {
        // if field is being signed, get stampLookupUrl from returned data
        let stampLookupUrlToUse = '';

        if (fields[0].type === c.ItemTypes.SIGNATURE) {
          stampLookupUrlToUse = data.data.fields.signatureFields[0].stampLookupUrl;
        } else {
          stampLookupUrlToUse = data.data.fields.initialFields[0].stampLookupUrl;
        }
        const { pages: updatedPages } = this.pageManager.updateFields(pages, [{ ...fields[0], stampLookupUrl: stampLookupUrlToUse }]);

        this.props.updatePages(updatedPages)

        this.setState({
          placedSignature: true,
        });

        return;
      }

      const { pages: updatedPages } = this.pageManager.updateFields(pages, fields);
      this.props.updatePages(updatedPages)
    }
  };

  handleFieldResize = async (id: number, width: number, height: number, pageIndex: number) => {
    const { pages, updateFields, createSnackbar } = this.props;
    const { activePage } = this.state;
    const docId = pages[activePage].documentId;
    const originalPages = [...pages];
    const { pages: updatedPages, field } = this.pageManager.updateField(this.props.pages, pageIndex, id, { width, height });

    this.props.updatePages(updatedPages)

    const [err] = await pro(updateFields([field], docId));

    if (err) {
      createSnackbar(c.CANNOT_UPDATE_FIELD);
      this.props.updatePages(originalPages)
    }
  };

  handleLineFieldUpdate = async (id: number, lineField: LineField, pageIndex: number) => {
    const {  pages, updateFields, createSnackbar } = this.props;
    const { activePage } = this.state;
    const {
      x1,
      y1,
      x2,
      y2,
      positionLock,
      strokeThickness,
    } = lineField;

    const originalField = pages[pageIndex].fields[id];
    const updatedFields = {};

    if (positionLock === true || positionLock === false) {
      updatedFields.positionLock = positionLock;
    }
    updatedFields.strokeThickness = strokeThickness;

    if (!originalField.positionLock) {
      updatedFields.x1 = x1;
      updatedFields.y1 = y1;
      updatedFields.x2 = x2;
      updatedFields.y2 = y2;
    }

    const { pages: updatedPages, field } = this.pageManager.updateField(pages, pageIndex, id, updatedFields);

    this.props.updatePages(updatedPages)
    const docId = pages[activePage].documentId;
    const [err] = await pro(updateFields([field], docId));

    if (err) {
      createSnackbar(c.CANNOT_UPDATE_FIELD);
      this.props.updatePages([...pages])

    }
  };

  handleFieldsDelete = async (ids: ?Array<number>) => {
    const { pages } = this.props;
    const { activeSelectionPageFields, activePage } = this.state;
    const docId = pages[activePage].documentId;

    // if ids been passed explicitly, use them, otherwise use activeSelection ids
    const idsToRemove = ids && ids.length ? ids : activeSelectionPageFields;

    const fields = [];

    idsToRemove.forEach((id) => {
      const field = pages[activePage].fields[id];
      fields.push(field);
    });
    const { pages: updatedPages } = this.pageManager.removeFields(pages, (activePage), idsToRemove);
    const [err] = await pro(this.props.updateFields(fields, docId, { deleting: true }));

    if (err) {
      this.props.createSnackbar(c.CANNOT_DELETE_FIELD);
    } else {

      this.props.updatePages(updatedPages)

      this.handleActiveSelectionClear();
    }
  };

  handleActiveSelectionRemove = (pageFieldId: number) => {

    const { activeSelectionPageFields, activeSelectionRelPos } = this.state;
    const newActiveSelectionPageFields = activeSelectionPageFields.filter(activeSelectionPageField => activeSelectionPageField !== pageFieldId);

    this.setState({
      activeSelectionPageFields: newActiveSelectionPageFields,
      activeSelectionRelPos: update(activeSelectionRelPos, { $unset: [pageFieldId] }),
    });
  };

  handleActiveSelectionClear = () => {
    this.setState({
      activeSelectionPageFields: [],
      activeSelectionRelPos: {},
      activeSelectionBox: { top: 0, left: 0 },
    });
  };

  // moving fields on the page or between pages
  // moveFieldId: id of field being moved
  // newY, newX - new coordinates relative to page top/left
  // sourcePageIndex: number of the page fields are being moved from
  // targetPageIndex: number of the page fields are being moved to
  // optionally it checks boundaries on move
  handleActiveSelectionMove = async (
    movedFieldId: number,
    newX: number,
    newY: number,
    sourcePageIndex: number,
    targetPageIndex: number,
    { checkBoundaries }: { checkBoundaries: boolean } = {},
  ) => {
    const {
     activeSelectionBox, activeSelectionPageFields, activeSelectionRelPos, activePage,
    } = this.state;
    const {
      pages,
    } = this.props;

    // Do not move fields between documents IDs, as it is currently unsupported by backend.
    // TODO: decide if we will allow movement between documents. Will require BE update.
    if (pages[targetPageIndex].documentId !== pages[sourcePageIndex].documentId) {
      return;
    }

    // ignore locked fields
    const activeSelectionPageFieldsToMove = activeSelectionPageFields.filter(id => !pages[sourcePageIndex].fields[id].positionLock);

    const { updateFields, createSnackbar } = this.props;

    const originalActiveSelectionBox = { ...activeSelectionBox };
    const originalPages = [...pages];
    const movedField = pages[sourcePageIndex].fields[movedFieldId];

    let newPages = {};
    let newActiveSelectionBox = {};
    let moveFields = [];
    let canMove = true;

    let moveDifference = {
      x: 0,
      y: 0,
    };

    // if moved field is of type line - get move difference b/w old and new line rectangles
    if (movedField.type === 'line') {
      const { x, y } = getLineRectangle(movedField.x1 || 0, movedField.y1 || 0, movedField.x2 || 0, movedField.y2 || 0);
      moveDifference = {
        x: newX - x,
        y: newY - y,
      };
    } else {
      moveDifference = {
        x: newX - (movedField.x || 0),
        y: newY - (movedField.y || 0),
      };
    }

    const adjustedZoom = this.state.zoom / ZOOM_PRECISION_FACTOR;

    if (sourcePageIndex === targetPageIndex) {

      if (checkBoundaries) {
        canMove = checkMoveBoundaries(
          pages,
          this.pageRefs,
          targetPageIndex,
          movedFieldId,
          activeSelectionPageFieldsToMove,
          activeSelectionRelPos,
          activeSelectionBox,
          adjustedZoom,
          newX * adjustedZoom,
          newY * adjustedZoom,
        );

        if (!canMove) {
          return;
        }
      }

      const { pages: updatedPages, fields } = this.pageManager.moveFields(
        pages, sourcePageIndex, moveDifference.x, moveDifference.y, activeSelectionPageFieldsToMove,
      );

      newActiveSelectionBox = {
        top: activeSelectionBox.top + moveDifference.y,
        left: activeSelectionBox.left + moveDifference.x,
      };
      newPages = updatedPages;
      moveFields = fields;

    } else {
      if (checkBoundaries) {
        canMove = checkMoveBoundaries(
          pages,
          this.pageRefs,
          targetPageIndex,
          movedFieldId,
          activeSelectionPageFieldsToMove,
          activeSelectionRelPos,
          activeSelectionBox,
          adjustedZoom,
          newX * adjustedZoom,
          newY * adjustedZoom,
          { sourcePageIndex },
        );

        if (!canMove) {
          return;
        }
      }

      const { pages: updatedPages, fields } = this.pageManager.moveFieldsBetweenPages(
        pages, sourcePageIndex, targetPageIndex, moveDifference.x, moveDifference.y, activeSelectionPageFieldsToMove,
      );
      newActiveSelectionBox = {
        top: newY - activeSelectionRelPos[movedFieldId].y,
        left: newX - activeSelectionRelPos[movedFieldId].x,
      };
      newPages = updatedPages;
      moveFields = fields;
    }

    this.props.updatePages(newPages)

    this.setState({
      activeSelectionBox: newActiveSelectionBox,
    });

    const docId = pages[activePage].documentId;
    const [err] = await pro(updateFields(moveFields, docId));

    if (err) {
      createSnackbar(c.CANNOT_UPDATE_FIELD);
      this.props.updatePages(originalPages)

      this.setState({
        activeSelectionBox: originalActiveSelectionBox,
      });
    }

  };

  updateFloatingFieldType = (floatingFieldType: string) => {
    this.setState({ floatingFieldType });
  };

  handleSelectedFieldsDuplicate = () => {
    this.copyFieldsToClipboard(this.state.activePage);
    const adjustedZoom = this.state.zoom / ZOOM_PRECISION_FACTOR;
    this.handleClipboardFieldsPaste(
      (this.state.activeSelectionBox.left + DUPLICATION_OFFSET) * adjustedZoom,
      (this.state.activeSelectionBox.top + DUPLICATION_OFFSET) * adjustedZoom,
    );
  };

  clearClipboard = () => {
    this.clipboardFields = [];
  };

  copyFieldsToClipboard = (pageIndex: number) => {
    const { pages } = this.props;
    this.clearClipboard();

    const newClipboardFields = [];
    if (this.state.activeSelectionPageFields.length) {
      this.state.activeSelectionPageFields.forEach((pageFieldId) => {
        const fieldToCopyPage = pages[pageIndex];
        const fieldToCopy = fieldToCopyPage.fields[pageFieldId];

        newClipboardFields.push({
          pageIndex,
          pageField: fieldToCopy,
          relativeLeft: this.state.activeSelectionRelPos[pageFieldId].x,
          relativeTop: this.state.activeSelectionRelPos[pageFieldId].y,
        });
      });
    }
    this.clipboardFields = newClipboardFields;
  };

  deleteActiveSelectionFields = () => {
    if (this.state.activeSelectionPageFields.length > 1) {
      const selectedFields = this.state.activeSelectionPageFields;
      this.props.createDialog({
        dialogContent: <ConfirmMultiDeleteDialog
          numFields={selectedFields.length}
          onSubmit={() => {
            this.handleFieldsDelete();
          }}
        />,
      });
    }
    if (this.state.activeSelectionPageFields.length === 1) {
      this.handleFieldsDelete();
    }
  };

  addFieldsToSelection = (ids: Array<number>, { resetSelection }: { resetSelection: boolean } = { resetSelection: false }) => {
    const {
      activeSelectionPageFields,
      activeSelectionRelPos,
      activeSelectionBox,
      activePage,
    } = this.state;
    const {
      pages,
    } = this.props;
    const oldPageFields = resetSelection ? [] : [...activeSelectionPageFields];

    const newPageFields = [];
    const newPageFieldsAbsPos = {};

    // If fields being added are from another page than fields already existing, should not use oldPageFields or oldPageFieldAbsPos.
    // switchingPage signals that new fields are being added from a different page than original.
    // Can use the pageId of the first field ([0]) in oldPageFields as all fields currently in activeSelection should be from the same page.
    const oldPageId = (oldPageFields[0]
      && pages[activePage].fields[oldPageFields[0]]
      && pages[activePage].fields[oldPageFields[0]].pageId)
        || -1;
    let switchingPage = false;
    if (ids[0] && pages[activePage].fields[ids[0]] && oldPageId !== pages[activePage].fields[ids[0]].pageId) {
      switchingPage = true;
    }

    ids.forEach((id) => {
      const field = pages[activePage].fields[id] || {}; // conditional protects against crash in event of field not existing yet

      newPageFields.push(id);

      const getLineTopLeftCorner = (x1: number, x2: number, y1: number, y2: number) => ({
        x: x1 < x2 ? x1 : x2,
        y: y1 < y2 ? y1 : y2,
      });
      newPageFieldsAbsPos[id] = {
        // in case of line, grab the top/left corner or line rectangle
        x: field.type === 'line' ? getLineTopLeftCorner(field.x1 || 0, field.x2 || 0, field.y1 || 0, field.y2 || 0).x : field.x,
        y: field.type === 'line' ? getLineTopLeftCorner(field.x1 || 0, field.x2 || 0, field.y1 || 0, field.y2 || 0).y : field.y,
      };
    });

    let allFields = [...newPageFields];
    let allPageFieldAbsPos = { ...newPageFieldsAbsPos };

    // If not switching page, add old fields to new selection as well.
    if (!switchingPage) {
      const oldPageFieldAbsPos = {};
      oldPageFields.forEach((fieldId) => {
        oldPageFieldAbsPos[fieldId] = {
          x: activeSelectionRelPos[fieldId].x + activeSelectionBox.left,
          y: activeSelectionRelPos[fieldId].y + activeSelectionBox.top,
        };
      });

      allFields = [...allFields, ...oldPageFields];
      allPageFieldAbsPos = { ...allPageFieldAbsPos, ...oldPageFieldAbsPos };
    }

    const {
      relativeFieldPositions, activeSelectionLeft, activeSelectionTop,
    } = determineRelativePositions(allPageFieldAbsPos);

    this.handleActiveSelectionClear();
    this.setState({
      activeSelectionPageFields: allFields,
      activeSelectionRelPos: relativeFieldPositions,
      activeSelectionBox: { left: activeSelectionLeft, top: activeSelectionTop },
    });
  };

  handleClipboardFieldsPaste = (leftWithZoom: number, topWithZoom: number) => {
    // Adjust top and left for zoom
    const top = topWithZoom / (this.state.zoom / ZOOM_PRECISION_FACTOR);
    const left = leftWithZoom / (this.state.zoom / ZOOM_PRECISION_FACTOR);

    const { pages } = this.props;
    const { activePage } = this.state;
    const page = pages[activePage];
    const pageWidth = page.width || 0;
    const pageHeight = page.height || 0;

    const newFields = [];
    const shifts = {
      xIncrease: 0,
      xDecrease: 0,
      yIncrease: 0,
      yDecrease: 0,
    };
    this.clipboardFields.forEach((clipboardField) => {
      const fieldToCopy = clipboardField.pageField;

      let width = 0;
      let height = 0;
      if (fieldToCopy.type === 'line') {
        // in case of line get the height and width of the rectangle formed by line
        const {
          x1, y1, x2, y2,
        } = fieldToCopy;
        height = Math.abs((y1 || 0) - (y2 || 0));
        width = Math.abs((x1 || 0) - (x2 || 0));
      } else {
        ({ width, height } = fieldToCopy);
      }

      const minY = (top + clipboardField.relativeTop);
      if ((minY < 0) && (shifts.yIncrease < minY)) {
        shifts.yIncrease = -minY;
      }
      const minX = (left + clipboardField.relativeLeft);
      if ((minX < 0) && (shifts.xIncrease < minX)) {
        shifts.xIncrease = -minX;
      }
      const maxY = (top + clipboardField.relativeTop + height);
      if ((maxY > pageHeight) && (shifts.yDecrease > pageHeight - maxY)) {
        shifts.yDecrease = pageHeight - maxY;
      }
      const maxX = (left + clipboardField.relativeLeft + width);
      if ((maxX > pageWidth) && (shifts.xDecrease > pageWidth - maxX)) {
        shifts.xDecrease = pageWidth - maxX;
      }

    });

    if (!(shifts.xIncrease > 0 && shifts.xDecrease < 0) || !(shifts.yIncrease > 0 && shifts.yDecrease < 0)) {
      this.clipboardFields.forEach((clipboardField) => {
        const fieldToCopy = clipboardField.pageField;

        const newTop = top + clipboardField.relativeTop + shifts.yIncrease + shifts.yDecrease;
        const newLeft = left + clipboardField.relativeLeft + shifts.xIncrease + shifts.xDecrease;
        newFields.push(this.cloneField(
          fieldToCopy,
          activePage,
          newTop,
          newLeft,
        ));

      });
    }
    this.createNewFields(newFields, activePage);
  };

  cloneField = (field: AdaptedField, pageIndex: number, newTop: number, newLeft: number) => {
    if (field.type === 'line') {
      const {
        fieldName, x2, y2, x1, y1, strokeThickness, color,
      } = field;
      const oldTop = (y1 || 0) < (y2 || 0) ? y1 : y2;
      const oldLeft = (x1 || 0) < (x2 || 0) ? x1 : x2;
      const moveDifference = { x: newLeft - (oldLeft || 0), y: newTop - (oldTop || 0) };

      return fieldTemplates.line({
        name: `${fieldName || ''}-Copy`,
        x: Math.round(x1 + moveDifference.x),
        y: Math.round(y1 + moveDifference.y),
        x2: Math.round(x2 + moveDifference.x),
        y2: Math.round(y2 + moveDifference.y),
        strokeThickness,
        color,
        positionLock: field.positionLock || false,
      });
    }

    const {
      type, fieldName, width, height, dataReference,
    } = field;

    const { documentId } = this.props.pages[pageIndex];
    const data = this.props.values[documentId] && this.props.values[documentId][(dataReference || -1)];

    return fieldTemplates[type]({
      name: `${fieldName || ''}-Copy`,
      y: Math.round(newTop),
      x: Math.round(newLeft),
      width: width || 0,
      height: height || 0,
      dataReference,
      assignee: field.assignee || '',
      data,
      positionLock: field.positionLock || false,
    });
  };

  createNewFields = async (newFields: Array<FieldTemplate>, pageIndex: number) => {
    const {
      createFields, createSnackbar, addValues, currentSignee,
    } = this.props;

    const currentPage = this.props.pages[pageIndex];
    const numFields = Object.keys(currentPage.fields).length;

    // Limit fields per page.
    if ((numFields + newFields.length) >= MAX_FIELDS_PER_PAGE) {
      createSnackbar(c.NO_MORE_FIELDS);
      return;
    }

    const { tempIds, pages: newPages } = this.pageManager.addTemporaryFields(this.props.pages, newFields, pageIndex);
    this.props.updatePages(newPages)

    const { documentId } = currentPage;
    const [err, fields] = await pro(createFields(newFields, this.props.pages[pageIndex].id, documentId));

    if (err || !fields) {
      // Remove temporary fields if there is a backend error.
      createSnackbar(c.CANNOT_ADD_FIELD);
      const { pages } = this.pageManager.removeFields(this.props.pages, pageIndex, tempIds);
      this.props.updatePages(pages)

      return;
    }
    const adaptedFields = adaptFields(get(fields, 'data.fields', {}), pageIndex);

    if (adaptedFields) {
      const newIds = [];
      const dataFields = {};
      const { pages } = this.pageManager.addFields(
        this.props.pages,
        adaptedFields,
        pageIndex,
        tempIds,
      );
      adaptedFields.forEach((adaptedField) => {
        const {
          id,
          dataReference,
          type,
        } = adaptedField;
        newIds.push(id);
        // Adding value to the document editor.
        if (dataReference && adaptedField.dataByDataReference && type !== 'line') {
          dataFields[dataReference] = {
            ...generateDataFieldByType(type, dataReference),
            value: adaptedField.dataByDataReference.value,
            assignee: currentSignee,
          };
        } else if (dataReference) {
          dataFields[dataReference] = { ...generateDataFieldByType(type, dataReference), assignee: currentSignee };
        }
      });

      addValues(dataFields, documentId);
      this.props.updatePages(pages)
      this.setState({
        activeSelectionPageFields: [],
        activeSelectionRelPos: {},
        activeSelectionBox: { top: 0, left: 0 },
      }, () => {
        this.addFieldsToSelection(newIds);
      });
    }
  };

  handleUpdateZoom = (addValue: number) => {
    const { zoom, zoomAdjustmentFactor } = this.state;
    let newZoom = zoom + addValue * zoomAdjustmentFactor;

    if (newZoom <= ZOOM_MINIMUM * zoomAdjustmentFactor) {
      newZoom = ZOOM_MINIMUM * zoomAdjustmentFactor;
    }
    if (newZoom >= ZOOM_MAXIMUM * zoomAdjustmentFactor) {
      newZoom = ZOOM_MAXIMUM * zoomAdjustmentFactor;
    }

    // If zoom changed, set scroll and setState
    if (newZoom !== zoom) {
      const adjustScrollByPercent = (zoom + addValue * zoomAdjustmentFactor) / zoom;

      // Temporarily set new scroll position so React Virtualized List uses it to set scrollTop inside List.
      this.setState({
        listScrollTop: adjustScrollByPercent * (this.listScrollValues.scrollTop),
      });

      this.setState({ zoom: newZoom });

      // Reset listScrollTop after scrollTop has updated so that React Virtualized List does not
      // stick to the same position.
      setTimeout(() => {
        this.setState({
          listScrollTop: null,
        });
      }, REACT_VIRTUALIZED_SCROLLTOP_UPDATE_TIME);
    }
  };

  storeListScrollValues = (items: Object) => {
    this.listScrollValues = items;
  };

  // Checks that all signees have at least one field assigned to them before proceeding.
  checkAllSigneesHaveField = () => {
    const { signees, createSnackbar } = this.props;

    if (!signees) {
      return true;
    }

    // signeeUnverifiedMap is a list of all signees. We have not yet verified
    // if they all have fields assigned to them in the document.
    const signeeUnverifiedMap = {};

    signees.forEach((signee) => {
      if (signee && signee.sessionSigneeName) {
        signeeUnverifiedMap[signee.sessionSigneeName] = true;
      }
    });

    if (!this.props.pages) {
      // Editor state and pages should always be present.
      createSnackbar(c.CANNOT_COMPLETE_SIGNING);
      return false;
    }

    // Iterate through all pages and fields.
    // As you find assignees, delete corresponding keys in signeeUnverifiedMap.
    // Break when signeeUnverifiedMap has no keys so you don't need to iterate
    // over all fields.
    let signeesVerified = false;
    const { pages } = this.props;
    const pagesLength = pages.length;
    for (let i = 0; i < pagesLength; i += 1) {
      if (signeesVerified) {
        break;
      }

      const page = pages[i];
      const fieldKeys = Object.keys(page.fields);
      const fieldKeysLength = fieldKeys.length;
      for (let j = 0; j < fieldKeysLength; j += 1) {
        if (signeesVerified) {
          break;
        }

        const fieldKey = fieldKeys[j];
        const field = page.fields[Number(fieldKey)];
        const assigneeToVerify = (field && field.assignee) || undefined;

        // Look for signee in map.
        if (assigneeToVerify && signeeUnverifiedMap[String(assigneeToVerify)]) {
          // If found, remove from map.
          delete signeeUnverifiedMap[String(assigneeToVerify)];
          // Check if there are no more keys.
          // Can break from loop and continue if there are no more keys.
          if (Object.keys(signeeUnverifiedMap).length === 0) {
            signeesVerified = true;
            break;
          }
        }
      }
    }

    let canProceed = true;
    if (Object.keys(signeeUnverifiedMap).length > 0) {
      // TODO: popup.
      canProceed = window.confirm('You have one or more signees that don’t have any fields assigned to them. Continue?',); // eslint-disable-line
    }

    return canProceed;
  };

  pageListRef: React.Element;

  pageListWidth: number;

  render() {
    const {
      pages,
      classes,
      mode,
      documents,
      signees,
      pdfPages,
      user,
      currentSignee,
      ...restProps
    } = this.props;
    const {
      activeSelectionPageFields,
      activeSelectionRelPos,
      activeSelectionBox,
      floatingFieldType,
      activePage,
      zoom,
      listScrollTop,
      placedSignature,
      zoomAdjustmentFactor,
    } = this.state;

    const currentUser = signees && signees.find(signee => signee && signee.userId === user.id);
    const uName = currentUser && currentUser.sessionSigneeName;

    return (
      <React.Fragment>
        <Toolbar
          zoom={zoom}
          floatingFieldType={floatingFieldType}
          updateFloatingFieldType={this.updateFloatingFieldType}
          mode={mode}
          handleUpdateZoom={this.handleUpdateZoom}
          signees={signees}
          currentSignee={currentSignee}
          onSigneeChange={this.props.onCurrentSigneeChange}
          colorMap={this.colorMap}
          zoomAdjustmentFactor={zoomAdjustmentFactor}
        />
        {
          mode === EDITOR_MODE.TEMPLATE_DRAFT
          && (
            <Banner>
              You are currently editing a draft of a form template. Users won&#39;t see the changes until it&#39;s published.
            </Banner>
          )
        }
        <div className={classes.panels}>
          <div
            className={classNames(classes.formPanel, classes.panel)}
            ref={(r) => {
              if (!this.pageListRef) {
                this.pageListRef = r;
              }
            }}
          >
            {
              pages
              && (
                <PageList
                  {...restProps}
                  signees={signees}
                  colorMap={this.colorMap}
                  pdfPages={pdfPages}
                  listScrollTop={listScrollTop}
                  storeListScrollValues={this.storeListScrollValues}
                  pages={pages}
                  activePage={activePage}
                  activeSelectionPageFields={activeSelectionPageFields}
                  activeSelectionRelPos={activeSelectionRelPos}
                  activeSelectionBox={activeSelectionBox}
                  setCurrentPageOnMouseMove={this.setCurrentPageOnMouseMove}
                  addFieldsToSelection={this.addFieldsToSelection}
                  deleteActiveSelectionFields={this.deleteActiveSelectionFields}
                  copyFieldsToClipboard={this.copyFieldsToClipboard}
                  handleFieldsDelete={this.handleFieldsDelete}
                  handleFieldResize={this.handleFieldResize}
                  handleLineFieldUpdate={this.handleLineFieldUpdate}
                  handleFieldAdd={this.handleFieldAdd}
                  handleFieldUpdate={this.handleFieldsUpdate}
                  handleActiveSelectionClear={this.handleActiveSelectionClear}
                  handleActiveSelectionMove={this.handleActiveSelectionMove}
                  handleActiveSelectionRemove={this.handleActiveSelectionRemove}
                  handleClipboardFieldClear={this.clearClipboard}
                  handleClipboardFieldsPaste={this.handleClipboardFieldsPaste}
                  handleSelectedFieldsDuplicate={this.handleSelectedFieldsDuplicate}
                  handleFieldsUpdate={this.handleFieldsUpdate}
                  handleUpdateZoom={this.handleUpdateZoom}
                  handleDeleteFields={this.deleteActiveSelectionFields}
                  floatingFieldType={floatingFieldType}
                  updateFloatingFieldType={this.updateFloatingFieldType}
                  zoom={zoom / ZOOM_PRECISION_FACTOR}
                  mode={mode}
                  passPageRefToEditor={this.passPageRefToEditor}
                  pageRefs={this.pageRefs}
                  currentUser={uName}
                  signed={placedSignature ? 1 : 0}
                />
              )
            }
          </div>
          <div className={classNames(classes.attributePanel, classes.panel)}>
            {
              <AttributesPanel
                zoom={zoom / ZOOM_PRECISION_FACTOR}
                pages={pages}
                activeSelectionPageFields={activeSelectionPageFields}
                handleFieldClone={this.handleSelectedFieldsDuplicate}
                handleFieldUpdate={this.handleFieldsUpdate}
                handleLineFieldUpdate={this.handleLineFieldUpdate}
                pageRefs={this.pageRefs}
                handleDeleteFields={this.deleteActiveSelectionFields}
                mode={mode}
                handleFieldResize={this.handleFieldResize}
                handleActiveSelectionMove={this.handleActiveSelectionMove}
                signees={signees}
              />
              }
          </div>
        </div>
      </React.Fragment>
    );
  }

}

export default compose(
  withPinchZoom({ disableBrowserZoom: true, enabledWidth: ['xs', 'sm'] }),
  withStyles(styles, { withTheme: true }),
  withRouter,
  withSnackbar,
  withDialog,
  withAppUser,
)(TemplateEditorC);
